import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import * as db from "../lib/db";

export function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set when we genuinely can't reach the auth backend, so the UI can say so
  // instead of spinning. Previously getSession() had no rejection path: if
  // Supabase was unreachable (paused project, dead network) the promise
  // rejected, setLoading(false) never ran, and the app showed loading dots
  // forever with no explanation.
  const [authError, setAuthError] = useState(null);
  // True while the user arrived via a password-reset link — show the
  // "set new password" screen instead of the normal app until they finish.
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
    const SESSION_TIMEOUT_MS = 12_000;
    let settled = false;

    // Hard ceiling: a hanging request must never leave the user on a spinner.
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      setAuthError("Couldn't reach the server. Check your connection and try again.");
      setLoading(false);
    }, SESSION_TIMEOUT_MS);

    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        // Auto-logout if session is older than 7 days
        if (s) {
          const issuedAt = s.token_issued_at || s.created_at;
          if (issuedAt && Date.now() - new Date(issuedAt).getTime() > SESSION_MAX_AGE_MS) {
            localStorage.clear();
            supabase.auth.signOut();
            setSession(null);
            setLoading(false);
            return;
          }
        }
        setSession(s);
        if (!s) setLoading(false);
      })
      .catch((e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        console.error("Auth session restore failed:", e);
        setAuthError("Couldn't reach the server. Check your connection and try again.");
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      // Supabase fires PASSWORD_RECOVERY when the page loads from a reset link.
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
      setAuthError(null);
      setSession(s);
      if (!s) setLoading(false);
    });

    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  const handleAuth = (newSession) => setSession(newSession);

  const handleSignOut = async () => {
    try {
      await db.signOut();
    } catch (e) {
      console.error("Sign out error:", e);
    }
    setSession(null);
  };

  return {
    session,
    loading,
    setLoading,
    userId: session?.user?.id,
    handleAuth,
    handleSignOut,
    authError,
    recovery,
    clearRecovery: () => setRecovery(false),
  };
}
